import type { QuestionOption } from '@/components/questionnaires/json-driven/types';

/** Max custom (additional) questions a nutritionist can add to one questionnaire. */
export const MAX_CUSTOM_QUESTIONS_PER_QUESTIONNAIRE = 6;

export type AdditionalQuestionResponseType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'select'
  | 'multi-select'
  | 'radio'
  | 'checkbox';

export const ADDITIONAL_QUESTION_RESPONSE_TYPES: Array<{
  value: AdditionalQuestionResponseType;
  label: string;
}> = [
  { value: 'text', label: 'Text Field' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select one' },
  { value: 'multi-select', label: 'Select all that apply' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
];

const OPTION_BASED_RESPONSE_TYPES = new Set<AdditionalQuestionResponseType>([
  'select',
  'multi-select',
  'radio',
  'checkbox',
]);

function slugifyOptionValue(label: string, index: number): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug.length > 0 ? slug : `option-${index + 1}`;
}

export function responseTypeRequiresOptions(type: AdditionalQuestionResponseType): boolean {
  return OPTION_BASED_RESPONSE_TYPES.has(type);
}

export function getResponseTypeLabel(type: AdditionalQuestionResponseType): string {
  return (
    ADDITIONAL_QUESTION_RESPONSE_TYPES.find((entry) => entry.value === type)?.label ??
    'Text Field'
  );
}

export function getResponseTypeInputType(type: AdditionalQuestionResponseType): string {
  if (type === 'text') return 'text';
  if (type === 'textarea') return 'textarea';
  if (type === 'number') return 'number';
  if (type === 'date') return 'date';
  return type;
}

export function buildQuestionOptions(optionLines: string): QuestionOption[] {
  const seen = new Set<string>();

  return optionLines
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((label, index) => {
      let value = slugifyOptionValue(label, index);

      if (seen.has(value)) {
        value = `${value}-${index + 1}`;
      }

      seen.add(value);
      return { label, value };
    });
}
